import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, User, Clock, Loader2, RefreshCw, MessageSquare, CheckCheck, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
  open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  awaiting_reply: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  awaiting_reply: 'Awaiting Reply',
  resolved: 'Resolved',
  closed: 'Closed',
};

export default function CustomerSupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/support-chat/tickets?${params}`);
      const ticketList = response.data.tickets || [];
      setTickets(ticketList);
      
      // Update selected ticket if it exists
      if (selectedTicket) {
        const updated = ticketList.find(t => t.id === selectedTicket.id);
        if (updated) {
          setSelectedTicket(updated);
        }
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedTicket?.id]);

  // Fetch messages for selected ticket
  const fetchMessages = useCallback(async (ticketId) => {
    if (!ticketId) return;
    setMessagesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/support-chat/tickets/${ticketId}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 10000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  // Fetch messages when ticket is selected
  useEffect(() => {
    if (selectedTicket) {
      fetchMessages(selectedTicket.id);
      // Poll for new messages every 3 seconds
      const interval = setInterval(() => fetchMessages(selectedTicket.id), 3000);
      return () => clearInterval(interval);
    }
  }, [selectedTicket?.id, fetchMessages]);

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setMessages([]);
    setInputValue('');
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || !selectedTicket || sending) return;

    setSending(true);
    try {
      await axios.post(`${API_URL}/api/support-chat/tickets/${selectedTicket.id}/reply`, {
        content: inputValue.trim()
      });
      
      setInputValue('');
      toast.success('Reply sent!');
      
      // Refresh messages and tickets
      await fetchMessages(selectedTicket.id);
      await fetchTickets();
      
    } catch (error) {
      toast.error('Failed to send reply');
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedTicket) return;
    try {
      await axios.put(`${API_URL}/api/support-chat/tickets/${selectedTicket.id}/status`, {
        status: newStatus
      });
      
      setSelectedTicket({ ...selectedTicket, status: newStatus });
      await fetchTickets();
      toast.success(`Status: ${STATUS_LABELS[newStatus]}`);
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Error updating status:', error);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="customer-support-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Customer Support</h1>
          <p className="text-sm text-slate-500 mt-1">
            Chat with Lunipin App users
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500/50"
          >
            <option value="all">All Tickets</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="awaiting_reply">Awaiting Reply</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <button
            onClick={() => { fetchTickets(); if(selectedTicket) fetchMessages(selectedTicket.id); }}
            className="p-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 overflow-hidden">
        {/* Tickets List */}
        <div className="w-80 bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden flex flex-col flex-shrink-0">
          <div className="p-3 border-b border-slate-800">
            <h2 className="text-sm font-medium text-slate-300">
              Tickets ({tickets.length})
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 text-green-400 animate-spin" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-500">
                <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm">No tickets</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {tickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => handleSelectTicket(t)}
                    className={`w-full p-3 text-left hover:bg-slate-800/50 transition-colors ${
                      selectedTicket?.id === t.id ? 'bg-slate-800/80 border-l-2 border-l-green-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {t.subject || 'No Subject'}
                        </p>
                        <p className="text-xs text-slate-500 truncate mt-0.5">
                          {t.user_name !== 'Unknown' ? t.user_name : (t.user_phone || 'User')}
                        </p>
                      </div>
                      {t.unread_by_support > 0 && (
                        <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                          {t.unread_by_support}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1 truncate">
                      {t.last_message || 'No messages'}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[t.status] || STATUS_COLORS.open}`}>
                        {STATUS_LABELS[t.status] || 'Open'}
                      </span>
                      <span className="text-xs text-slate-600">
                        {formatTime(t.last_message_time || t.updated_at)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 bg-slate-900/50 border border-slate-800 rounded-lg flex flex-col overflow-hidden">
          {!selectedTicket ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500">
              <MessageSquare className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-lg">Select a ticket</p>
              <p className="text-sm mt-1">Choose from the list to view conversation</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white">
                    {selectedTicket.subject || 'Support Chat'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {selectedTicket.user_name !== 'Unknown' ? selectedTicket.user_name : 'User'} 
                    {selectedTicket.user_phone && ` • ${selectedTicket.user_phone}`}
                  </p>
                </div>
                <select
                  value={selectedTicket.status || 'open'}
                  onChange={(e) => handleUpdateStatus(e.target.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${STATUS_COLORS[selectedTicket.status]} bg-transparent focus:outline-none cursor-pointer`}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="awaiting_reply">Awaiting Reply</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messagesLoading && messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 text-green-400 animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    <p>No messages yet</p>
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={msg.id || index}
                      className={`flex ${msg.sender_type === 'support' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex gap-2 max-w-[75%] ${msg.sender_type === 'support' ? 'flex-row-reverse' : ''}`}>
                        {/* Avatar */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          msg.sender_type === 'support'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {msg.sender_type === 'support' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <User className="w-4 h-4" />
                          )}
                        </div>

                        {/* Message Bubble */}
                        <div className={`rounded-lg px-3 py-2 ${
                          msg.sender_type === 'support'
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-800 text-slate-200'
                        }`}>
                          <p className={`text-xs mb-1 ${
                            msg.sender_type === 'support' ? 'text-green-200' : 'text-slate-500'
                          }`}>
                            {msg.sender_type === 'support' 
                              ? (msg.sender_name || 'Support')
                              : (msg.sender_name !== 'Unknown' ? msg.sender_name : 'User')}
                          </p>
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {msg.content}
                          </p>
                          <div className={`flex items-center gap-1 mt-1 text-xs ${
                            msg.sender_type === 'support' ? 'text-green-200' : 'text-slate-500'
                          }`}>
                            <Clock className="w-3 h-3" />
                            <span>{formatTime(msg.created_at)}</span>
                            {msg.sender_type === 'support' && (
                              <CheckCheck className="w-3 h-3 ml-1" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' ? (
                <form onSubmit={handleSendReply} className="p-3 border-t border-slate-800">
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Type your message..."
                      disabled={sending}
                      className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 disabled:opacity-50"
                      data-testid="reply-input"
                    />
                    <button
                      type="submit"
                      disabled={!inputValue.trim() || sending}
                      className="px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors flex items-center gap-2"
                      data-testid="send-reply-button"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="p-3 border-t border-slate-800 bg-slate-800/50">
                  <p className="text-center text-sm text-slate-500">
                    Ticket closed. Change status to reply.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
